import PageHeader from "@/components/pageHeader";
import Magnetic from "@/components/ui/magnetic";
import { Briefcase, MapPin, Clock, User, ChevronLeft, ChevronRight, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const CareersPage = () => {
  const openings = [
    { title: "Senior Sales Manager", department: "Sales", location: "Mohali", type: "Full-time", experience: "5-8 years" },
    { title: "Site Engineer", department: "Construction", location: "Mohali", type: "Full-time", experience: "3-5 years" },
    { title: "Marketing Executive", department: "Marketing", location: "Mohali", type: "Full-time", experience: "2-4 years" },
    { title: "HR Executive", department: "Human Resources", location: "Mohali", type: "Full-time", experience: "2-3 years" },
  ];
  const benefits = ["Competitive Salary", "Health Insurance", "Performance Bonuses", "Professional Development", "Work-Life Balance", "Growth Opportunities"];
  const [openForm, setOpenForm] = useState(false);
  const navigate = useNavigate();

  // FORM STATES
  const [sending, setSending] = useState(false);
  const [utmSource, setUtmSource] = useState("google");
  const [formError, setFormError] = useState({});
  const generateCaptcha = () => {
    return Math.random()
      .toString(36)
      .substring(2, 8);
  };

  const [captchaText, setCaptchaText] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const currentDate = new Date()
    .toLocaleDateString("en-GB")
    .replace(/-/g, "/");

  const initialValues = {
    name: "",
    email: "",
    position: "",
    resume: null,
    date: currentDate,
    leadSource: utmSource,
  };

  const [data, setData] = useState(initialValues);

  // UTM
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utm_source =
      urlParams.get("utm_source") || "google";

    setUtmSource(utm_source);

    setData((prevData) => ({
      ...prevData,
      leadSource: utm_source,
    }));
  }, []);
  useEffect(() => {
    if (openForm) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [openForm]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const validate = (values) => {
    const error = {};

    if (!values.name)
      error.name = "Name is required";

    if (!values.email) {
      error.email = "Email is required";
    } else {
      const emailRegex =
        /^\w+([\.-]?\w+)@\w+([\.-]?\w+)(\.\w{2,3})+$/;

      if (!emailRegex.test(values.email)) {
        error.email = "Invalid email";
      }
    }
    if (captchaInput !== captchaText) {
      error.captcha = "Invalid captcha";
    }

    if (!values.position)
      error.position = "Position is required";

    if (!values.resume) {
      error.resume = "Resume is required";
    } else if (values.resume instanceof File) {
      if (values.resume.size > 5 * 1024 * 1024) {
        error.resume = "File size should not exceed 5MB";
      } else if (!values.resume.name.toLowerCase().endsWith(".pdf") && values.resume.type !== "application/pdf") {
        error.resume = "Only PDF files are allowed";
      }
    }

    return error;
  }

  const sendMail = async (e) => {
    e.preventDefault();

    const validationErrors = validate(data);

    setFormError(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setSending(true);

      let resumeBase64 = "";

      if (data.resume) {
        resumeBase64 = await convertToBase64(data.resume);

        // remove base64 prefix
        resumeBase64 = resumeBase64.split(",")[1];
      }

      const emailBody = `
<div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; background-color: #ffffff; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);">
  <!-- Premium Header -->
  <div style="background-color: #1A6F53; padding: 28px 32px; border-bottom: 4px solid #f59e0b;">
    <h2 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;">GILLCO</h2>
    <p style="color: #1A6f53; margin: 6px 0 0 0; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;">Careers Portal / Application</p>
  </div>

  <!-- Content -->
  <div style="padding: 32px; background-color: #ffffff;">
    <p style="color: #307e64; font-size: 15px; line-height: 1.6; margin-top: 0; margin-bottom: 28px;">A new candidate has successfully submitted an application through the careers portal. Details are listed below:</p>

    <table style="width: 100%; border-collapse: collapse;">
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 600; width: 32%;">Candidate Name</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 500;">${data.name}</td>
      </tr>
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 600;">Email Address</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 500;"><a href="mailto:${data.email}" style="color: #3b82f6; text-decoration: none; font-weight: 600;">${data.email}</a></td>
      </tr>
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 600;">Target Position</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 600;"><span style="background-color: #fef3c7; color: #d97706; padding: 4px 10px; border-radius: 6px; font-size: 13px; font-weight: 600;">${data.position}</span></td>
      </tr>
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 600;">Date Submitted</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #307e64; font-size: 14px; font-weight: 500;">${data.date}</td>
      </tr>
    </table>
  </div>

  <!-- Footer -->
  <div style="background-color: #f8fafc; padding: 24px 32px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b;">
    <div style="display: flex; justify-content: space-between; flex-wrap: wrap;">
      <div style="margin-bottom: 8px; margin-right: 16px;"><strong>Lead Source:</strong> ${data.leadSource || "google"}</div>
    </div>
    <div style="margin-top: 16px; border-top: 1px dashed #e2e8f0; padding-top: 16px; text-align: center; color: #94a3b8;">
      This email is automatically generated by the Gillco Group Careers System.
    </div>
  </div>
</div>
`.replace(/\r?\n/g, " ");

      const sanitizedFileName = data.resume ? data.resume.name.replace(/[^a-zA-Z0-9.-]/g, "") : "";

      const endpoint = process.env.REACT_APP_ENDPOINT_MAIL_URL;
      const mailURL = `${window.location.origin}${endpoint}?token=${process.env.REACT_APP_SEND_MAIL_ATTACHMENT_TOKEN}`;

      const res = await fetch(mailURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject: "Resume Application",
          body: emailBody,
          emailAttachments: data.resume
            ? [
              {
                fileName: sanitizedFileName,
                content: resumeBase64,
              },
            ]
            : []
        })
        ,
      });

      await res.text();

      if (res.ok) {
        // alert("Application submitted successfully");

        setData(initialValues);

        setOpenForm(false);
        navigate('/thankyou');
      } else {
        alert("Failed to send application");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong");
    } finally {
      setSending(false);
    }
  };

  // INPUT CHANGE
  const change = (e) => {
    const { name, value, files } = e.target;

    // FILE VALIDATION
    if (files && files[0]) {
      const file = files[0];

      // Check if it is a PDF
      if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
        setFormError((prev) => ({
          ...prev,
          resume: "Only PDF files are allowed",
        }));

        e.target.value = "";

        setData((prev) => ({
          ...prev,
          resume: null,
        }));

        return;
      }

      // 5MB limit
      const maxSize = 5 * 1024 * 1024;

      if (file.size > maxSize) {
        setFormError((prev) => ({
          ...prev,
          resume: "File size should not exceed 5MB",
        }));

        e.target.value = "";

        setData((prev) => ({
          ...prev,
          resume: null,
        }));

        return;
      } else {
        // Clear file error if file is within limit
        setFormError((prev) => ({
          ...prev,
          resume: undefined,
        }));
      }
    }

    setData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
      leadSource: utmSource,
    }));
  };

  return (
    <div data-testid="v13-careers-page">
      <PageHeader title="Careers" subtitle="Join Our Team" backgroundImage="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920" />
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-body text-xs tracking-[0.2em] uppercase text-[#C4A265] mb-4 block">Work With Us</span>
              <h2 className="text-3xl sm:text-4xl text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Build Your Career at Gillco</h2>
              <div className="w-16 h-0.5 bg-[#C4A265] mb-8" />
              <p className="font-body text-base text-[#4A4A4A] leading-relaxed mb-6">At Gillco Group, we believe our people are our greatest asset. We're always looking for talented individuals who share our passion for excellence and our commitment to building communities.</p>
              <p className="font-body text-base text-[#4A4A4A] leading-relaxed mb-8">Join Punjab's leading real estate developer and be part of a legacy that builds dreams.</p>
              <h3 className="text-xl text-[#0A0A0A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Why Join Us?</h3>
              <div className="grid grid-cols-2 gap-3">{benefits.map((b, i) => (<div key={i} className="flex items-center gap-2"><div className="w-2 h-2 bg-[#C4A265]" /><span className="font-body text-sm text-[#4A4A4A]">{b}</span></div>))}</div>
            </div>
            <div><img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800" alt="Gillco Team" className="w-full h-[500px] object-cover" /></div>
          </div>
        </div>
      </section>
      <section className="py-20 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16"><span className="font-body text-xs tracking-[0.2em] uppercase text-[#C4A265]">Opportunities</span><h2 className="text-3xl sm:text-4xl text-[#0A0A0A] mt-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Current Openings</h2><div className="w-16 h-0.5 bg-[#C4A265] mx-auto mt-6" /></div>
          <div className="space-y-4">
            {openings.map((j, i) => (
              <div key={i} className="bg-white p-6 border-l-2 border-[#C4A265] hover:shadow-lg transition-shadow flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div><h3 className="text-xl text-[#0A0A0A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{j.title}</h3><div className="flex flex-wrap gap-4 text-sm"><span className="flex items-center gap-1 text-[#4A4A4A]"><Briefcase size={14} /> {j.department}</span><span className="flex items-center gap-1 text-[#4A4A4A]"><MapPin size={14} /> {j.location}</span><span className="flex items-center gap-1 text-[#4A4A4A]"><Clock size={14} /> {j.type}</span><span className="flex items-center gap-1 text-[#4A4A4A]"><User size={14} /> {j.experience}</span></div></div>
                {/* <a href={`mailto:careers@gillcogroup.com?subject=Application for ${j.title}`} className="inline-flex items-center gap-2 bg-[#0A4F3B] text-white px-6 py-3 text-sm tracking-wider uppercase hover:bg-[#1A6F53] transition-colors flex-shrink-0">Apply Now <ChevronRight size={14} /></a> */}
                <button
                  onClick={() => {
                    setData((prev) => ({
                      ...prev,
                      position: j.title,
                    }));

                    setOpenForm(true);
                  }}
                  className="inline-flex items-center gap-2 bg-[#0A4F3B] text-white px-6 py-3 text-sm tracking-wider uppercase hover:bg-[#1A6F53] transition-colors flex-shrink-0"
                >
                  Apply Now <ChevronRight size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center bg-white p-12"><h3 className="text-2xl text-[#0A0A0A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Don't See a Suitable Position?</h3><p className="font-body text-[#4A4A4A] mb-8 max-w-2xl mx-auto">We're always looking for exceptional talent. Send us your resume and we'll keep you in mind for future opportunities.</p><button type="button"
            onClick={() => { setData((prev) => ({ ...prev, position: "General Application" })); setOpenForm(true); }} className="inline-flex items-center gap-3 bg-[#0A4F3B] text-white px-8 py-4 text-sm tracking-[0.1em] uppercase hover:bg-[#1A6F53] transition-colors"
          >Send Your Resume<Send size={16} /></button></div>
        </div>
      </section>
      {openForm && (
        <div className="fixed inset-0 !z-[100] bg-black/70 backdrop-blur-sm overflow-hidden">
          <div className="min-h-[100dvh] flex items-start justify-center px-4 py-20 max-sm:py-25 max-sm:min-h-[80dvh]">
            <div className="relative w-full max-w-3xl">
              {/* CLOSE */}

              <button
              onClick={() => setOpenForm(false)}
              className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white text-navy-900 flex items-center justify-center shadow-lg"
            >
              <X className="w-5 h-5" />
            </button>

              {/* FORM */}
              {/* <form
                onSubmit={sendMail}
                data-testid="v4-contact-form"
                className="max-h-[80vh] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-7 max-sm:p-[1.25rem] md:p-[1.25rem] md:mt-[14rem] lg:mt-[2rem] xl:mt-[4rem] shadow-[0_8px_40px_-12px_rgba(10,37,64,0.1)]"              >
                <h3 className="font-display text-2xl md:text-3xl font-bold text-navy-900 tracking-tight">
                  Apply for Position
                </h3>

                <p className="mt-2 text-sm text-slate-600">
                  Share your details — our hiring
                  team will contact you.
                </p>

                <div className="mt-8 max-sm:mt-[.9rem] grid grid-cols-1 sm:grid-cols-2 gap-4">
                 
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Full Name *
                    </label>

                    <input
                      required
                      type="text"
                      name="name"
                      value={data.name}
                      onChange={change}
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-3 max-sm:pt-0 text-navy-900 placeholder:text-slate-400 transition-colors"
                      placeholder="Enter your Name"
                    />

                    {formError.name && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.name}
                      </p>
                    )}
                  </div>

                
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Email *
                    </label>

                    <input
                      required
                      type="email"
                      name="email"
                      value={data.email}
                      onChange={change}
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-3 max-sm:pt-0 text-navy-900 placeholder:text-slate-400 transition-colors"
                      placeholder="Enter your Email Address"
                    />

                    {formError.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Position *
                    </label>

                    <input
                      required
                      type="text"
                      name="position"
                      value={data.position}
                      readOnly
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 outline-none py-3  max-sm:pt-0 text-navy-900"
                    />

                    {formError.position && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.position}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Resume (Max 5MB) *
                    </label>

                    <input
                      required
                      type="file"
                      name="resume"
                      accept=".pdf"
                      onChange={change}
                      className="mt-3 block w-full text-sm text-navy-900"
                    />

                    {formError.resume ? (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.resume}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-[10px] mt-1">
                        Accepted format: PDF (Max 5MB)
                      </p>
                    )}
                  </div>


                  <div className="sm:col-span-2 mt-1">
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Captcha *
                    </label>

                    <div className="mt-3 flex items-center gap-3 flex-nowrap">
                      <input
                        type="text"
                        placeholder="Enter Captcha"
                        value={captchaInput}
                        onChange={(e) =>
                          setCaptchaInput(e.target.value)
                        }
                        className="w-[180px] max-sm:w-[150px] bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-2 text-navy-900 placeholder:text-slate-400 transition-colors"
                      />

                      <button
                        type="button"
                        onClick={() => {
                          setCaptchaText(generateCaptcha());
                          setCaptchaInput("");
                        }}
                        className="text-2xl text-navy-900 shrink-0"
                      >
                        ↻
                      </button>

                      <div className="text-[1.25rem] tracking-[0.25em] text-navy-900 font-semibold italic select-none whitespace-nowrap">
                        {captchaText}
                      </div>
                    </div>

                    {formError.captcha && (
                      <p className="text-red-500 text-xs mt-2">
                        {formError.captcha}
                      </p>
                    )}
                  </div>
                </div>
                <div className=" bottom-0  pt-6 mt-8 ">
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#0A4F3B] text-white py-4 rounded-full"
                  >
                    {sending ? "Sending..." : "Submit Application"}
                  </button>
                </div>
              </form> */}
                          <form onSubmit={sendMail} className="p-6 bg-white">
                                          <h3 className="font-display text-2xl md:text-3xl font-bold text-navy-900 tracking-tight">
                  Apply for Position
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Share your details — our hiring
                  team will contact you.
                </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                <div>
                  <label className="block mb-2 text-sm font-medium">
                   full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={data.name}
                    onChange={change}
                    className="w-full border border-gray-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#0A4F3B]"
                  />
                    {formError.name && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.name}
                      </p>
                    )}     
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={data.email}
                    onChange={change}
                    className="w-full border border-gray-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#0A4F3B]"
                  />
                  {formError.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.email}
                      </p>
                    )}
                </div>
                   <div>
                  <label className="block mb-2 text-sm font-medium">
                    Position *
                  </label>
                  <input
                    type="text"
                    name="position"
                    required
                    value={data.position}
                    readOnly
                    onChange={change}
                    className="w-full border border-gray-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#0A4F3B]"
                  />
                  {formError.position && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.position}
                      </p>
                    )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium">
                    Resume *
                  </label>
                  <input
                    type="file"
                    name="resume"
                    accept=".pdf"
                    required
                    onChange={change}
                    className="w-full border border-gray-300 px-4 py-3 rounded-md focus:outline-none focus:border-[#0A4F3B]"
                  />
                   {formError.resume ? (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.resume}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-[10px] mt-1">
                        Accepted format: PDF (Max 5MB)
                      </p>
                    )}
                </div>

  
                                 <div className="mt-3 flex items-center gap-3 flex-nowrap">
                      <input
                        type="text"
                        placeholder="Enter Captcha"
                        value={captchaInput}
                        onChange={(e) =>
                          setCaptchaInput(e.target.value)
                        }
                        className="w-[170px] max-sm:w-[150px] bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-2 text-navy-900 placeholder:text-slate-400 transition-colors"
                      />

                      <button
                        type="button"
                        onClick={() => {
                          setCaptchaText(generateCaptcha());
                          setCaptchaInput("");
                        }}
                        className="text-2xl text-navy-900 shrink-0"
                      >
                        ↻
                      </button>

                      <div className="text-[1.25rem] tracking-[0.25em] text-navy-900 font-semibold italic select-none whitespace-nowrap">
                        {captchaText}
                      </div>
                    </div>

                    {formError.captcha && (
                      <p className="text-red-500 text-xs mt-2">
                        {formError.captcha}
                      </p>
                    )}
                  </div>


              <button
                type="submit"
                disabled={sending}
                className="mt-6 w-full bg-[#0A4F3B] text-white py-4 uppercase tracking-wider hover:bg-[#1A6F53] transition-colors"
              >
                  {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};